import { useState } from "react";
import { Play, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { runPython } from "@/lib/pyodideLoader";
import { runOnPiston, isPistonLanguage } from "@/lib/pistonRunner";

const PY_ALIASES = new Set(["python", "py", "python3"]);

const isRunnable = (language) => {
  if (!language) return false;
  return PY_ALIASES.has(language.toLowerCase()) || isPistonLanguage(language);
};

/**
 * A fenced code block from a chat message. For Python, adds a "Run" button that
 * executes the snippet via Pyodide entirely in the browser.
 */
export const CodeBlock = ({ code, language }) => {
  const isPython = language && PY_ALIASES.has(language.toLowerCase());
  const runnable = isRunnable(language);
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);

  const onRun = async () => {
    setRunning(true);
    setOutput(null);
    const result = isPython
      ? await runPython(code)
      : await runOnPiston(code, language);
    setOutput(result);
    setRunning(false);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't access clipboard — try selecting the code manually.");
    }
  };

  return (
    <div className="my-2 rounded-xl overflow-hidden border border-foreground/10" data-testid="code-block">
      {(runnable || language) && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-foreground/95 text-background/70 text-xs">
          <span className="font-mono uppercase tracking-wider">{language || "code"}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onCopy}
              data-testid="code-copy-btn"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded hover:bg-background/10"
              title="Copy code"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
            {runnable && (
              <button
                type="button"
                onClick={onRun}
                disabled={running}
                data-testid="code-run-btn"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[hsl(var(--brimi-mint))] text-foreground font-semibold hover:bg-[hsl(var(--brimi-mint))]/90 disabled:opacity-60"
              >
                {running ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Play className="w-3 h-3 fill-current" />
                )}
                {running ? "Running…" : "Run"}
              </button>
            )}
          </div>
        </div>
      )}
      <pre className="bg-foreground/90 text-background text-xs sm:text-sm p-3 overflow-x-auto font-mono whitespace-pre">
        <code>{code}</code>
      </pre>
      {output && (
        <div
          data-testid="code-output"
          className={`px-3 py-2 text-xs sm:text-sm font-mono whitespace-pre-wrap border-t border-foreground/10 max-h-[35vh] overflow-y-auto ${
            output.ok ? "bg-[hsl(var(--brimi-mint))]/20" : "bg-destructive/10"
          }`}
        >
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
            {output.ok ? "Output" : "Error"}
          </div>
          {output.stdout && <div>{output.stdout}</div>}
          {output.stderr && <div className="text-destructive">{output.stderr}</div>}
          {output.error && !output.ok && (
            <div className="text-destructive mt-1">{output.error}</div>
          )}
          {output.ok && !output.stdout && !output.stderr && (
            <div className="opacity-60 italic">(no output — try adding a print() statement)</div>
          )}
        </div>
      )}
    </div>
  );
};
